import React, { useState, useEffect, useRef } from 'react'
import { useTheme } from '@react-navigation/native'
import { Icon, Text } from '@components'
import { StyleSheet, TouchableOpacity, View, Animated } from 'react-native'
import { Theme } from '@theme'

type UserConsent = {
  title: string,
  value: boolean
  onChange: (value: boolean) => void
}

export const UserConsent = ({ title, value, onChange }: UserConsent) => {
  const theme = useTheme()
  const styles = getStyle(theme)

  const [isSelect, setIsSelect] = useState(value)
  const scaleAnim = useRef(new Animated.Value(0)).current
  const opacityAnim = useRef(new Animated.Value(0)).current

  useEffect(() => {
    let scaleAnimation: Animated.CompositeAnimation | null = null
    let opacityAnimation: Animated.CompositeAnimation | null = null

    if (isSelect) {
      const animations = Animated.parallel([
        scaleAnimation = Animated.spring(scaleAnim, {
          toValue: 1,
          useNativeDriver: true,
          tension: 150,
          friction: 4,
        }),
        opacityAnimation = Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
      ])
      animations.start()
    } else {
      const animations = Animated.parallel([
        scaleAnimation = Animated.timing(scaleAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
        opacityAnimation = Animated.timing(opacityAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
      ])
      animations.start()
    }

    // Cleanup function pour arrêter les animations si le composant se démonte
    return () => {
      scaleAnimation?.stop()
      opacityAnimation?.stop()
    }
  }, [isSelect, scaleAnim, opacityAnim])

  // Cleanup général au démontage du composant
  useEffect(() => {
    return () => {
      // Arrêter toutes les animations en cours
      scaleAnim.stopAnimation()
      opacityAnim.stopAnimation()
    }
  }, [scaleAnim, opacityAnim])

  return (
    <TouchableOpacity style={styles.container} onPress={() => {
      setIsSelect(!isSelect)
      onChange(!isSelect)
    }}>
      <View style={[
        styles.iconContainer,
        { borderColor: isSelect ? theme.colors.primary : theme.colors.lightGrey }
      ]}>
        {isSelect && (
          <Animated.View
            style={{
              transform: [{ scale: scaleAnim }],
              opacity: opacityAnim,
            }}
          >
            <Icon name="check" size={theme.iconSize.small} color={theme.colors.primary} />
          </Animated.View>
        )}
      </View>
      <Text type="bodySmall" color={theme.colors.lightGrey}>
        {title}
      </Text>
    </TouchableOpacity>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      paddingTop: theme.spacing.xxxl,
      paddingBottom: theme.spacing.xl,
      alignItems: 'center',
      flexDirection: 'row',
    },
    iconContainer: {
      width: 20,
      height: 20,
      borderWidth: 2,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: theme.border.tiny,
      marginRight: theme.spacing.sm,
    },
  })
